import { Link } from 'react-router-dom'
import {
  CalendarCheck,
  Lightbulb,
  Wrench,
  Users,
  Briefcase,
  Heart,
  ArrowRight,
} from 'lucide-react'
import LoopCTAs from '../../components/LoopCTAs'
import FloatingBackground from '../../components/FloatingBackground'
import Seo from '../../components/Seo'
import { FadeIn } from '../../components/Motion'
import { trackCTA } from '../../utils/analytics'
import { FINAL_CTA_HEADLINE } from '../../config/cta'

const loops = [
  {
    icon: CalendarCheck,
    title: 'Event Loop',
    outer: 'Plan → Execute → Retro → Improve',
    description: 'Multi-stakeholder feedback across organizers, attendees, sponsors, vendors, and staff.',
    href: '/feedback-loops/event',
    color: '#3b82f6',
  },
  {
    icon: Lightbulb,
    title: 'Validation Loop',
    outer: 'Hypothesize → Test → Learn → Pivot',
    description: 'Product-market fit, value prop, and concept validation. For founders, venture studios, and innovation arms.',
    href: '/feedback-loops/validation',
    color: '#8b5cf6',
  },
  {
    icon: Wrench,
    title: 'Product Loop',
    outer: 'Build → Ship → Test → Refine',
    description: 'Rapid product iteration. Includes in-product embed (beta) for feedback at the point of use.',
    href: '/feedback-loops/product',
    color: '#00008B',
  },
  {
    icon: Users,
    title: 'Customer Loop',
    outer: 'Acquire → Onboard → Serve → Retain → Win-back',
    description: 'NPS follow-up, churn diagnostics, onboarding, win-back. Across the full customer lifecycle.',
    href: '/feedback-loops/customer',
    color: '#3b82f6',
  },
  {
    icon: Briefcase,
    title: 'Service Loop',
    outer: 'Deliver → Listen → Improve → Re-engage',
    description: 'Long-term client relationships, account health, professional services, agencies.',
    href: '/feedback-loops/service',
    color: '#8b5cf6',
  },
  {
    icon: Heart,
    title: 'Employee Loop',
    outer: 'Hire → Engage → Develop → Retain',
    description: 'Engagement, onboarding, exit interviews, rollout retros. VoE waitlist available.',
    href: '/feedback-loops/employee',
    color: '#00008B',
  },
]

export default function FeedbackLoopsIndex() {
  return (
    <div>
      <Seo
        title="Feedback Loops — Six Ways to Close the Loop"
        description="Event, validation, product, customer, service, and employee feedback loops. Conversational surveys that surface novel perspectives and turn feedback into action."
        path="/feedback-loops"
      />
      <section className="relative bg-white py-20 md:py-28 overflow-hidden">
        <FloatingBackground />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-4">
              The feedback loop catalog
            </p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-intelligence-900 mb-6 tracking-tight leading-[0.95]">
              Feedback to{' '}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B] bg-clip-text text-transparent">
                  action
                </span>
                <span
                  aria-hidden="true"
                  className="absolute left-0 right-0 bottom-1 md:bottom-2 h-3 md:h-4 bg-[#8b5cf6]/25 -z-0 -skew-y-1"
                />
              </span>
              <span className="text-[#00008B]">.</span>
            </h1>
            <p className="text-xl md:text-2xl text-intelligence-700 max-w-3xl mx-auto">
              Close the loop you&apos;re already running. Whichever loop that is.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loops.map((loop, i) => (
              <FadeIn key={loop.title} delay={i * 0.05}>
                <div className="relative group h-full">
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 rounded-xl translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2.5 group-hover:translate-y-2.5"
                    style={{ backgroundColor: loop.color }}
                  />
                  <Link
                    to={loop.href}
                    onClick={() => trackCTA(loop.title, `feedback-loops-index-${loop.title.toLowerCase().replace(/\s+/g, '-')}`)}
                    className="relative block h-full bg-white rounded-xl border-2 border-intelligence-900 p-6"
                  >
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 border-2 border-intelligence-900"
                      style={{ backgroundColor: `${loop.color}15` }}
                    >
                      <loop.icon className="w-6 h-6" style={{ color: loop.color }} />
                    </div>
                    <h3 className="text-xl font-bold text-intelligence-900 mb-2">{loop.title}</h3>
                    <p
                      className="font-mono text-[11px] uppercase tracking-widest mb-3"
                      style={{ color: loop.color }}
                    >
                      {loop.outer}
                    </p>
                    <p className="text-intelligence-600 mb-4">{loop.description}</p>
                    <span className="font-semibold inline-flex items-center text-intelligence-900 gap-1 group-hover:gap-2 transition-all">
                      Explore loop <ArrowRight className="w-4 h-4" />
                    </span>
                  </Link>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-intelligence-900 py-20 md:py-24 relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B]" />
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-r from-[#00008B] via-[#8b5cf6] to-[#3b82f6]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-400 mb-3">
              Your move
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
              {FINAL_CTA_HEADLINE}
            </h2>
            <LoopCTAs location="feedback-loops-index-final" align="center" />
          </FadeIn>
        </div>
      </section>
    </div>
  )
}
